import {
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  input,
  OnDestroy,
  OnInit,
  Output,
  untracked,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from '@env/environment';
import { FeedbackConsoleInterceptorService } from '@services/helpers/feedback-console-interceptor.service';
import { FeedbackService } from '@services/http/feedback.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { FeedbackScreenshotEditorComponent } from '@shared/components/floating-feedback-button/feedback-screenshot-editor/feedback-screenshot-editor.component';
import { FeedbackVideoPlayerComponent } from '@shared/components/floating-feedback-button/feedback-video-player/feedback-video-player.component';

interface AttachmentFile {
  file: File;
  name: string;
  size: string;
}

@Component({
  selector: 'app-feedback-panel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FeedbackScreenshotEditorComponent,
    FeedbackVideoPlayerComponent,
  ],
  templateUrl: './feedback-panel.component.html',
  styleUrls: ['./feedback-panel.component.scss'],
})
export class FeedbackPanelComponent implements OnInit, OnDestroy {
  @ViewChild('videoPlayer') videoPlayerRef?: ElementRef<HTMLVideoElement>;

  screenshotBlob = input<Blob | null>(null);
  isOpen = input<boolean>(false);
  isCapturingScreenshot = input<boolean>(false);

  /** Painel fechado (nome que não colide com o evento `close` do DOM). */
  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<void>();
  /** Primeira captura: disparada pelo clique do usuário no card de screenshot. */
  @Output() initialCaptureScreenshot = new EventEmitter<void>();
  /** Nova captura html2canvas sem fechar o painel (host excluído do raster). */
  @Output() recaptureScreenshot = new EventEmitter<void>();
  /** Solicita ao pai que descarte o blob de screenshot atual. */
  @Output() clearScreenshot = new EventEmitter<void>();
  /** Emite true quando gravação inicia, false quando para. */
  @Output() recordingStateChanged = new EventEmitter<boolean>();

  form!: FormGroup;
  isSubmitting = false;
  /** Exibe a tela de sucesso após envio (sem link ao backoffice — público cliente). */
  showSubmitSuccess = false;

  showScreenshotEditor = false;
  screenshotConfirmed: Blob | null = null;
  screenshotPreviewUrl: string | null = null;

  isRecording = false;
  isSavingRecording = false;
  hasMicAudio = false;
  showMicSelection = false;
  micDevices: MediaDeviceInfo[] = [];
  recordingFile: File | null = null;
  recordingPreviewUrl: string | null = null;
  isVideoPlaying = false;
  showVideoPlayer = false;
  private mediaStream: MediaStream | null = null;
  private micStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordingChunks: Blob[] = [];

  otherAttachments: AttachmentFile[] = [];

  private pendingOpenScreenshotEditorAfterRecapture = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly feedbackService: FeedbackService,
    private readonly consoleInterceptor: FeedbackConsoleInterceptorService,
    private readonly toastService: ToastHelperService,
    private readonly cdr: ChangeDetectorRef
  ) {
    effect(() => {
      const open = this.isOpen();
      const capturing = this.isCapturingScreenshot();
      const blob = this.screenshotBlob();

      if (!this.pendingOpenScreenshotEditorAfterRecapture) {
        return;
      }

      if (!open) {
        untracked(() => {
          this.pendingOpenScreenshotEditorAfterRecapture = false;
        });
        return;
      }

      if (capturing) {
        return;
      }

      if (!blob) {
        untracked(() => {
          this.pendingOpenScreenshotEditorAfterRecapture = false;
        });
        return;
      }

      untracked(() => {
        this.pendingOpenScreenshotEditorAfterRecapture = false;
        this.openScreenshotEditor();
      });
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      type: [null, Validators.required],
      description: ['', [Validators.required, Validators.maxLength(1000)]],
    });
    void this.loadMicDevices();
  }

  private async loadMicDevices(): Promise<void> {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      this.micDevices = all.filter(
        d => d.kind === 'audioinput' && d.deviceId !== 'default' && d.deviceId !== 'communications'
      );
      this.cdr.markForCheck();
    } catch {
      this.micDevices = [];
    }
  }

  get selectedType(): string | null {
    return this.form.get('type')?.value ?? null;
  }

  get descriptionLength(): number {
    return this.form.get('description')?.value?.length ?? 0;
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  selectType(type: 'Bug' | 'Suggestion'): void {
    this.form.patchValue({ type });
  }

  openScreenshotEditor(): void {
    if (!this.screenshotBlob() && !this.screenshotConfirmed) return;
    this.showScreenshotEditor = true;
  }

  onScreenshotCardMainClick(): void {
    if (this.isCapturingScreenshot()) return;
    if (!this.screenshotBlob() && !this.screenshotConfirmed) {
      this.pendingOpenScreenshotEditorAfterRecapture = true;
      this.initialCaptureScreenshot.emit();
      return;
    }
    this.openScreenshotEditor();
  }

  onScreenshotCardMainKeydown(event: KeyboardEvent): void {
    if (this.isCapturingScreenshot()) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.screenshotBlob() && !this.screenshotConfirmed) {
        this.pendingOpenScreenshotEditorAfterRecapture = true;
        this.initialCaptureScreenshot.emit();
      } else {
        this.openScreenshotEditor();
      }
    }
  }

  onRecaptureScreenshotClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isCapturingScreenshot()) return;
    this.clearScreenshotConfirmationOnly();
    this.pendingOpenScreenshotEditorAfterRecapture = true;
    this.recaptureScreenshot.emit();
  }

  /** Limpa apenas o print confirmado no editor; o blob inicial vem do pai após recaptura. */
  private clearScreenshotConfirmationOnly(): void {
    this.screenshotConfirmed = null;
    if (this.screenshotPreviewUrl) {
      URL.revokeObjectURL(this.screenshotPreviewUrl);
      this.screenshotPreviewUrl = null;
    }
  }

  onScreenshotConfirmed(blob: Blob): void {
    if (this.screenshotPreviewUrl) {
      URL.revokeObjectURL(this.screenshotPreviewUrl);
    }
    this.screenshotConfirmed = blob;
    this.screenshotPreviewUrl = URL.createObjectURL(blob);
    this.showScreenshotEditor = false;
  }

  onScreenshotCancelled(): void {
    this.showScreenshotEditor = false;
  }

  get activeScreenshotBlob(): Blob | null {
    return this.screenshotConfirmed ?? this.screenshotBlob();
  }

  onRecordingCardClick(): void {
    if (this.isSavingRecording) return;
    if (this.isRecording) {
      this.stopRecording();
      return;
    }
    if (this.recordingFile) {
      this.openVideoPlayer();
      return;
    }
    if (this.showMicSelection) {
      this.showMicSelection = false;
      return;
    }
    this.showMicSelection = true;
    void this.loadMicDevices();
  }

  async startRecordingWithMic(deviceId: string | null): Promise<void> {
    this.showMicSelection = false;
    await this.startRecording(deviceId);
  }

  // deviceId: null = sem áudio, '' = padrão do sistema, 'uuid' = dispositivo específico
  private async startRecording(micDeviceId: string | null): Promise<void> {
    try {
      this.mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: false,
        // preferCurrentTab pré-seleciona a aba atual no seletor do navegador (Chrome 107+)
        // não elimina o diálogo, mas o usuário só precisa confirmar com um clique
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
      } as DisplayMediaStreamOptions);

      let micTrack: MediaStreamTrack | null = null;
      if (micDeviceId !== null) {
        try {
          const audioConstraints: MediaTrackConstraints = micDeviceId
            ? { deviceId: { exact: micDeviceId } }
            : {};
          this.micStream = await navigator.mediaDevices.getUserMedia({
            audio: audioConstraints,
            video: false,
          });
          micTrack = this.micStream.getAudioTracks()[0] ?? null;
          // Recarrega a lista após permissão — os labels só ficam disponíveis depois do primeiro getUserMedia
          void this.loadMicDevices();
        } catch {
          this.micStream = null;
        }
      }
      this.hasMicAudio = !!micTrack;

      const tracks: MediaStreamTrack[] = [...this.mediaStream.getVideoTracks()];
      if (micTrack) tracks.push(micTrack);
      const combinedStream = new MediaStream(tracks);

      this.recordingChunks = [];
      this.mediaRecorder = new MediaRecorder(combinedStream);

      this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) this.recordingChunks.push(e.data);
      };

      this.mediaRecorder.onstop = () => {
        if (this.isSavingRecording) {
          const blob = new Blob(this.recordingChunks, { type: 'video/webm' });
          this.recordingFile = new File([blob], 'gravacao-tela.webm', { type: 'video/webm' });
          this.revokeRecordingPreview();
          this.recordingPreviewUrl = URL.createObjectURL(blob);
          this.isSavingRecording = false;
          this.openVideoPlayer();
          this.cdr.detectChanges();
        }
        this.cleanupStream();
      };

      this.mediaStream.getVideoTracks()[0].onended = () => {
        if (this.isRecording) this.stopRecording();
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      this.recordingStateChanged.emit(true);
    } catch {
      this.isRecording = false;
    }
  }

  private stopRecording(save = true): void {
    if (save) this.isSavingRecording = true;
    this.mediaRecorder?.stop();
    this.isRecording = false;
    this.recordingStateChanged.emit(false);
  }

  private cleanupStream(): void {
    this.mediaStream?.getTracks().forEach(t => t.stop());
    this.mediaStream = null;
    this.micStream?.getTracks().forEach(t => t.stop());
    this.micStream = null;
    this.mediaRecorder = null;
    this.hasMicAudio = false;
  }

  openVideoPlayer(): void {
    if (this.recordingPreviewUrl) this.showVideoPlayer = true;
  }

  onVideoPlayerClosed(): void {
    this.showVideoPlayer = false;
  }

  toggleVideoPlay(): void {
    const video = this.videoPlayerRef?.nativeElement;
    if (!video) return;
    if (video.paused) {
      video.play();
      this.isVideoPlaying = true;
    } else {
      video.pause();
      this.isVideoPlaying = false;
    }
  }

  onVideoEnded(): void {
    this.isVideoPlaying = false;
  }

  removeRecording(): void {
    this.showVideoPlayer = false;
    this.videoPlayerRef?.nativeElement?.pause();
    this.isVideoPlaying = false;
    this.revokeRecordingPreview();
    this.recordingFile = null;
  }

  private revokeRecordingPreview(): void {
    if (this.recordingPreviewUrl) {
      URL.revokeObjectURL(this.recordingPreviewUrl);
      this.recordingPreviewUrl = null;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    Array.from(input.files).forEach(file => {
      this.otherAttachments.push({
        file,
        name: file.name,
        size: this.formatSize(file.size),
      });
    });
    input.value = '';
  }

  removeAttachment(index: number): void {
    this.otherAttachments.splice(index, 1);
  }

  get hasContent(): boolean {
    return (
      this.form.dirty ||
      !!this.screenshotBlob() ||
      !!this.screenshotConfirmed ||
      !!this.recordingFile ||
      this.isRecording ||
      this.otherAttachments.length > 0
    );
  }

  onClose(): void {
    this.showSubmitSuccess = false;
    this.showMicSelection = false;
    this.pendingOpenScreenshotEditorAfterRecapture = false;
    this.closed.emit();
  }

  clearAll(): void {
    if (this.isRecording) this.stopRecording();
    this.showSubmitSuccess = false;
    this.clearScreenshot.emit();
    this.resetForm();
  }

  submit(): void {
    if (!this.form.valid || this.isSubmitting) return;
    this.isSubmitting = true;

    const screenshotFile = this.screenshotConfirmed
      ? new File([this.screenshotConfirmed], 'screenshot.png', { type: 'image/png' })
      : this.screenshotBlob()
        ? new File([this.screenshotBlob()!], 'screenshot.png', { type: 'image/png' })
        : undefined;

    const errors = this.consoleInterceptor.getErrors();

    const request = {
      type: this.form.value.type as 'Bug' | 'Suggestion',
      description: this.form.value.description as string,
      route: window.location.pathname,
      fullUrl: window.location.href,
      userAgent: navigator.userAgent,
      build: environment.buildNumber,
      consoleErrors: JSON.stringify(errors),
      screenshot: screenshotFile,
      recording: this.recordingFile ?? undefined,
      attachments: this.otherAttachments.map(a => a.file),
    };

    this.feedbackService.submitFeedback(request).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.resetForm();
        this.showSubmitSuccess = true;
        this.submitted.emit();
      },
      error: () => {
        this.isSubmitting = false;
        this.toastService.showToastError('Não foi possível enviar o feedback. Tente novamente.');
      },
    });
  }

  ngOnDestroy(): void {
    if (this.isRecording) this.stopRecording(false);
    this.isSavingRecording = false;
    this.cleanupStream();
    this.revokeRecordingPreview();
    if (this.screenshotPreviewUrl) {
      URL.revokeObjectURL(this.screenshotPreviewUrl);
    }
  }

  private resetForm(): void {
    this.form.reset();
    this.isSavingRecording = false;
    this.screenshotConfirmed = null;
    if (this.screenshotPreviewUrl) {
      URL.revokeObjectURL(this.screenshotPreviewUrl);
      this.screenshotPreviewUrl = null;
    }
    this.videoPlayerRef?.nativeElement?.pause();
    this.isVideoPlaying = false;
    this.revokeRecordingPreview();
    this.recordingFile = null;
    this.otherAttachments = [];
    this.showScreenshotEditor = false;
    this.showMicSelection = false;
    this.pendingOpenScreenshotEditorAfterRecapture = false;
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
